import React, { useState } from 'react';
import {
    Box,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    CircularProgress,
    Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import { getAutoSeating } from '@openapi/ticket-seats/ticket-seats';

function BulkAddTicketsWizard({
    timeslotId,
    slotKey = null,
    allTicketTypes,
    allTicketOptions,
    priceOverrides,
    existingItems,
    isTicketCombinationAllowed = () => true,
    delayedSeatAssignment = false,
    onAddItems,
}) {
    const { t } = useTranslation();
    const [quantity, setQuantity] = useState(1);
    const [ticketTypeId, setTicketTypeId] = useState('');
    const [ticketOptionId, setTicketOptionId] = useState('');
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    const [error, setError] = useState(null);
    const allowedOptionsForSelectedType = allTicketOptions?.filter((option) => {
        // Rental slot scoping: options bound to a different slot are hidden.
        // Options without a slot (and non-rental timeslots) always pass.
        if (option.slotKey && option.slotKey !== slotKey) {
            return false;
        }
        if (!ticketTypeId) {
            return true;
        }
        return isTicketCombinationAllowed(ticketTypeId, option.id);
    });

    const handleAdd = async () => {
        // Validate inputs
        if (quantity < 1 || quantity > 100) {
            setError('Please enter a quantity between 1 and 100');
            return;
        }

        if (!ticketTypeId || !ticketOptionId) {
            setError('Please select both ticket type and option');
            return;
        }

        setIsLoadingSuggestions(true);
        setError(null);

        try {
            const selectedTicketType = allTicketTypes?.find((type) => type.id === ticketTypeId);
            const selectedTicketOption = allTicketOptions?.find((opt) => opt.id === ticketOptionId);

            // Check if ticket type requires seats
            const requiresSeats =
                !delayedSeatAssignment &&
                selectedTicketType?.seats &&
                selectedTicketType.seats.length > 0;

            let suggestedSeats = [];

            if (requiresSeats) {
                // Call auto-seating API
                const currentSeats = existingItems
                    .filter((item) => item.ticketSeatId)
                    .map((item) => ({
                        id: item.ticketSeatId,
                        ticketTypeId: item.ticketTypeId,
                    }));

                const response = await getAutoSeating(timeslotId, {
                    currentSeats,
                    requiredSeats: [
                        {
                            ticketTypeId,
                            quantity: parseInt(quantity, 10),
                        },
                    ],
                });

                if (response?.suggestions && response.suggestions.length > 0) {
                    const suggestion = response.suggestions.find(
                        (s) => s.ticketTypeId === ticketTypeId,
                    );
                    if (suggestion?.seats) {
                        suggestedSeats = suggestion.seats.slice(0, quantity);
                    }
                }

                // Check if we got enough seats
                if (suggestedSeats.length < quantity) {
                    setError(
                        `Could only find ${suggestedSeats.length} available seats out of ${quantity} requested`,
                    );
                    setIsLoadingSuggestions(false);
                    return;
                }
            }

            // Prefer the per-timeslot override, fall back to the show default.
            const overridePrice = priceOverrides?.get(`${ticketTypeId}:${ticketOptionId}`);
            const seedPriceCents =
                overridePrice !== undefined
                    ? overridePrice
                    : selectedTicketType?.priceMatrix?.[selectedTicketOption?.name] || 0;

            // Create new items
            const newItems = [];
            for (let i = 0; i < quantity; i++) {
                const seat = suggestedSeats[i];
                newItems.push({
                    id: Math.random().toString(36).substring(2, 15),
                    ticketSeatId: seat?.id || null,
                    ticketSeatName: seat?.name || '',
                    ticketTypeId,
                    ticketTypeName: selectedTicketType?.name || '',
                    ticketOptionId,
                    ticketOptionName: selectedTicketOption?.name || '',
                    customPriceCents: seedPriceCents,
                    note: '',
                    checkinNote: '',
                    shouldDelete: false,
                    ticketTypeCanBuyAlone: selectedTicketType?.canBuyAlone || false,
                });
            }

            onAddItems(newItems);
            // Reset form
            setQuantity(1);
            setTicketTypeId('');
            setTicketOptionId('');
        } catch (err) {
            console.error('Error creating bulk tickets:', err);
            setError(`Failed to create tickets: ${err.message || 'Unknown error'}`);
        } finally {
            setIsLoadingSuggestions(false);
        }
    };

    return (
        <Box sx={{ mt: 2 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1,
                    alignItems: { xs: 'stretch', sm: 'flex-start' },
                }}
            >
                <TextField
                    type="number"
                    label={t('bookingitems.columns.quantity')}
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 1)}
                    inputProps={{ min: 1, max: 100 }}
                    sx={{ width: { xs: '100%', sm: 100 } }}
                    size="small"
                />

                <FormControl sx={{ flex: 1 }} size="small">
                    <InputLabel>{t('bookingitems.columns.ticketTypeName')}</InputLabel>
                    <Select
                        value={ticketTypeId}
                        onChange={(e) => {
                            const nextTicketTypeId = e.target.value;
                            setTicketTypeId(nextTicketTypeId);
                            if (
                                ticketOptionId &&
                                !isTicketCombinationAllowed(nextTicketTypeId, ticketOptionId)
                            ) {
                                setTicketOptionId('');
                            }
                        }}
                        label={t('bookingitems.columns.ticketTypeName')}
                    >
                        {allTicketTypes?.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                                {type.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ flex: 1 }} size="small">
                    <InputLabel>{t('bookingitems.columns.ticketOptionName')}</InputLabel>
                    <Select
                        value={ticketOptionId}
                        onChange={(e) => setTicketOptionId(e.target.value)}
                        label={t('bookingitems.columns.ticketOptionName')}
                    >
                        {allowedOptionsForSelectedType?.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <IconButton
                    onClick={handleAdd}
                    color="primary"
                    disabled={
                        !ticketTypeId ||
                        !ticketOptionId ||
                        isLoadingSuggestions ||
                        quantity < 1 ||
                        quantity > 100
                    }
                    size="small"
                    sx={{ alignSelf: { xs: 'flex-end', sm: 'auto' } }}
                >
                    {isLoadingSuggestions ? (
                        <CircularProgress size={20} />
                    ) : (
                        <AddIcon sx={{ fontSize: 34 }} />
                    )}
                </IconButton>
            </Box>
        </Box>
    );
}

export default BulkAddTicketsWizard;
